import { useState, useCallback } from 'react';
import type { ComputedSlot } from '@vagusx/clients';
import { getAppointmentClient } from '@/lib/api-clients';
import type { SchedulingChatMessage, SlotSearchParams } from '../types';

/**
 * Serialize form search params to a natural language context string.
 */
function formToContext(params: SlotSearchParams): string {
  const parts: string[] = [];
  if (params.patientType === 'new') parts.push('for a new patient');
  else parts.push('for an existing patient');
  if (params.timeBlocks.length > 0) parts.push(`during ${params.timeBlocks.join(' or ')}`);
  parts.push(`between ${params.startDate} and ${params.endDate}`);
  if (params.providerIds.length > 0) parts.push('with specific provider(s)');
  else parts.push('with any available provider');
  return `Looking for appointment slots ${parts.join(', ')}.`;
}

interface UseSchedulingChatResult {
  messages: SchedulingChatMessage[];
  isLoading: boolean;
  sendMessage: (text: string) => Promise<void>;
  reset: (context: SlotSearchParams) => void;
  latestSlots: ComputedSlot[];
}

/**
 * Hook for the scheduling chat interface.
 * Calls POST /api/appointments/scheduling-chat via AppointmentClient.
 */
export function useSchedulingChat(
  initialContext: SlotSearchParams
): UseSchedulingChatResult {
  const [messages, setMessages] = useState<SchedulingChatMessage[]>(() => [
    {
      role: 'assistant',
      content: `I can help you find available slots. ${formToContext(initialContext)} What are you looking for?`,
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [latestSlots, setLatestSlots] = useState<ComputedSlot[]>([]);
  const [context] = useState(initialContext);

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      if (!text.trim() || isLoading) return;

      const userMessage: SchedulingChatMessage = { role: 'user', content: text };
      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const data = await getAppointmentClient().schedulingChat({
          message: text,
          context: {
            startDate: context.startDate,
            endDate: context.endDate,
            patientType: context.patientType,
            timeBlocks: context.timeBlocks,
            providerIds: context.providerIds,
            locationIds: context.locationIds,
            appointmentTypeId: context.appointmentTypeId ?? undefined,
          },
          conversationHistory: messages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
        });

        const assistantMessage: SchedulingChatMessage = {
          role: 'assistant',
          content: data.reply,
          slots: data.slots.length > 0 ? data.slots : undefined,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        if (data.slots && data.slots.length > 0) {
          setLatestSlots(data.slots);
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Sorry, I had trouble processing your request. Please try again.',
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [context, messages, isLoading]
  );

  const reset = (newContext: SlotSearchParams): void => {
    setMessages([
      {
        role: 'assistant',
        content: `I can help you find available slots. ${formToContext(newContext)} What are you looking for?`,
      },
    ]);
    setLatestSlots([]);
  };

  return { messages, isLoading, sendMessage, reset, latestSlots };
}
